import {ThemeVariableType} from '@toolify/client/src/modules/theme/enum/ThemeVariableType'
import {IThemeVariables} from '@toolify/client/src/modules/theme/types/IThemeVariables'
import {TextCanvasTableElementAlignType} from '@toolify/client/src/modules/tables/strategies/CanvasTableElementStrategies/TextCanvasTableElementStrategy/enum/TextCanvasTableElementAlignType'
import {IconWeightType} from '@toolify/client/src/modules/ui/enum/IconWeightType'
import {TimeTrackerDropdownLayoutType} from '@toolify/client/src/modules/databases/enum/TimeTrackerDropdownLayoutType'
import {ChoosePropertyTypeLayoutType} from '@toolify/client/src/modules/databases/enum/ChoosePropertyTypeLayoutType'
import {StatusDropdownLayoutType} from '@toolify/client/src/modules/databases/enum/StatusDropdownLayoutType'
import {ItemGroupHeaderLayoutType} from '@toolify/client/src/modules/ui/enum/ItemGroupHeaderLayoutType'
import {
  FiordNightDerivedVariableType,
  getFiordNightDerivedVariables,
} from '@toolify/client/src/modules/theme/functions/getFiordNightDerivedVariables'

const PaletteColors: string[] = [
  '#8A0012',
  '#AA3602',
  '#A45E03',
  '#987E00',

  '#6A8F02',
  '#138A00',
  '#0C6801',
  '#00856E',

  '#0269A0',
  '#2334AB',
  '#5601AE',
  '#700188',

  '#80005E',
  '#465164',
  '#6C778B',
  '#8F98AA',
]

const PaletteColorMatrix = [
  [0, 1, 2, 3],
  [4, 5, 6, 7],
  [8, 9, 10, 11],
  [12, 13, 14, 15],
]

const FiordNightBaseThemeVariables: Omit<IThemeVariables, FiordNightDerivedVariableType> = {
  [ThemeVariableType.PrimaryColor]: '#558FEF',
  [ThemeVariableType.PrimaryHoverColor]: '#6C9EF1',
  [ThemeVariableType.OnPrimaryColor]: '#D1DFF6',
  [ThemeVariableType.OnPrimaryHoverColor]: '#EAEEF6',
  [ThemeVariableType.PrimaryContainerColor]: '#092B60',
  [ThemeVariableType.PrimaryContainerHoverColor]: '#134390',
  [ThemeVariableType.OnPrimaryContainerColor]: '#6C9EF1',

  [ThemeVariableType.SecondaryColor]: '#545F73',
  [ThemeVariableType.OnSecondaryColor]: '#E0E3E9',
  [ThemeVariableType.SecondaryContainerColor]: '#465164',
  [ThemeVariableType.OnSecondaryContainerColor]: '#EEEFF3',

  [ThemeVariableType.TertiaryColor]: '#9200C4',
  [ThemeVariableType.OnTertiaryColor]: '#E4B5FF',
  [ThemeVariableType.TertiaryContainerColor]: '#580079',
  [ThemeVariableType.OnTertiaryContainerColor]: '#EDD3FD',

  [ThemeVariableType.ErrorColor]: '#FBBBB5',
  [ThemeVariableType.OnErrorColor]: '#73000D',
  [ThemeVariableType.ErrorContainerColor]: '#470005',
  [ThemeVariableType.OnErrorContainerColor]: '#FBD3CF',

  [ThemeVariableType.SurfaceDimColor]: '#05080D',
  [ThemeVariableType.SurfaceColor]: '#10151D',
  [ThemeVariableType.DatabaseTableGridColor]: '#10151D',
  [ThemeVariableType.DatabaseTableFontWeightOffset]: 0,
  [ThemeVariableType.DatabaseTableNameFontWeightOffset]: 0,
  [ThemeVariableType.DatabaseTableColumnHeaderFontWeightOffset]: 0,
  [ThemeVariableType.DatabaseTableNameColor]: '',
  [ThemeVariableType.SurfaceBrightColor]: '#12171AFF',

  [ThemeVariableType.ContainerHighestColor]: '#3F4A5D',
  [ThemeVariableType.ContainerHighColor]: '#2C3747',
  [ThemeVariableType.ContainerColor]: '#222B3A',
  [ThemeVariableType.ContainerLowColor]: '#1A212D',
  [ThemeVariableType.ContainerLowestColor]: '#070B13',

  [ThemeVariableType.ContentBrightColor]: '#F4F5F8',
  [ThemeVariableType.ContentLightColor]: '#E0E3E9',
  [ThemeVariableType.ContentNormalColor]: '#A1A9B9',
  [ThemeVariableType.ContentDimColor]: '#7D8799',
  [ThemeVariableType.ContentDarkColor]: '#646F84',
  [ThemeVariableType.ContentDarkestColor]: '#2C3747',

  [ThemeVariableType.BorderRadius]: 4,
  [ThemeVariableType.CheckboxBorderRadius]: 4,
  [ThemeVariableType.Shadow]: '#56638026',
  [ThemeVariableType.MobileFontSizeFactor]: 1.125,

  [ThemeVariableType.DatabaseTableHeaderDefaultBackgroundColor]: '#222B3A',
  [ThemeVariableType.DatabaseTableHeaderHoverBackgroundColor]: '#2C3747',
  [ThemeVariableType.DatabaseTableStickyShadowColor]: '#10151D',
  [ThemeVariableType.DatabaseTableScrollShadowColor]: '#070B13',
  [ThemeVariableType.DatabaseTableCardBorderColor]: 'rgba(0, 0, 0, 0)',
  [ThemeVariableType.DatabaseTableGroupElevation]: 'none',
  [ThemeVariableType.DatabaseTableStickyHeaderElevation]: 'none',
  [ThemeVariableType.DatabaseTablePinnedColumnElevation]: 'none',
  [ThemeVariableType.UseDatabaseTableCardLayout]: false,
  [ThemeVariableType.DatabaseCellHoverBackgroundColor]: '#222B3A',
  [ThemeVariableType.DatabaseCellHoverBorder]: '0.125rem solid var(--container-highest-color)',
  [ThemeVariableType.DatabaseCellFocusHoverBorder]: '0.125rem solid var(--primary-hover-color)',
  [ThemeVariableType.DatabaseRelationIconBackgroundColor]: '#222B3A',
  [ThemeVariableType.DatabaseRelationIconBackgroundHoverColor]: '#2C3747',
  [ThemeVariableType.DatabaseTableColumnNameAlignment]: TextCanvasTableElementAlignType.Center,
  [ThemeVariableType.FileIconWeightType]: IconWeightType.Regular,
  [ThemeVariableType.NotificationIconWeightType]: IconWeightType.Regular,
  [ThemeVariableType.PreferredIconWeightType]: IconWeightType.Regular,
  [ThemeVariableType.DatabaseHeaderGroupDropdownPlacement]: 'bottom-start',
  [ThemeVariableType.DashboardRecentListBackgroundColor]: 'var(--container-low-color)',
  [ThemeVariableType.DashboardRecentListHorizontalPadding]: '0.5rem',
  [ThemeVariableType.ShowDashboardRecentItemBorders]: true,
  [ThemeVariableType.UseStickyDashboardResourcesButton]: false,
  [ThemeVariableType.ShouldShowDashboardAndKanbanInContainer]: false,
  [ThemeVariableType.UseDetachedDashboardWidgetTypePreview]: false,
  [ThemeVariableType.CanCreateConversationDashboardWidget]: false,

  [ThemeVariableType.UseScrollbarGradients]: true,
  [ThemeVariableType.UseModernAuthLayout]: false,
  [ThemeVariableType.PreferBorders]: true,
  [ThemeVariableType.UseLightBadgeColors]: false,
  // kolor zrodla plakietki light ponizej tej chromy = szarosc: napis bez wymuszonego minimum nasycenia
  // (0 = zawsze z minimum; nadpisuje Grafit, ktorego szarosci palety sa neutralne)
  [ThemeVariableType.LightBadgeNeutralChromaThreshold]: 0,
  [ThemeVariableType.UseUnifiedMarkdownEditorBackground]: false,
  [ThemeVariableType.ShouldUseLineStyleInFilters]: false,
  [ThemeVariableType.ShowNewMessageButtonOutsideInput]: true,
  [ThemeVariableType.ShowSidebarWorkspaceDropdownToggle]: true,
  [ThemeVariableType.ShowSidebarFavouriteIconChips]: true,
  [ThemeVariableType.ShowCloudItemDetails]: true,
  [ThemeVariableType.ShowCloudItemIcon]: false,
  [ThemeVariableType.ShowEmptyFilesPlaceholderVisual]: false,
  [ThemeVariableType.ShowEmptyConversationPlaceholderVisual]: false,
  [ThemeVariableType.ShowWorkspaceInviteAsButton]: false,
  [ThemeVariableType.ShouldShowModalHeaderBackgrounds]: true,
  [ThemeVariableType.ShouldShowBreadcrumbsTitleFirst]: true,
  [ThemeVariableType.UsePaletteColorsInAvatars]: false,
  [ThemeVariableType.ShouldClampNotificationTitleItem]: true,
  [ThemeVariableType.ShowNotificationResourceSpace]: true,
  [ThemeVariableType.UseCenteredTimeTrackerCellLayout]: true,
  [ThemeVariableType.TimeTrackerDropdownLayoutType]: TimeTrackerDropdownLayoutType.Legacy,
  [ThemeVariableType.ChoosePropertyTypeLayoutType]: ChoosePropertyTypeLayoutType.Legacy,
  [ThemeVariableType.StatusDropdownLayoutType]: StatusDropdownLayoutType.Legacy,
  [ThemeVariableType.ItemGroupHeaderLayoutType]: ItemGroupHeaderLayoutType.Legacy,

  // T-946: dotychczasowe wartosci wpisane na sztywno w komponentach
  [ThemeVariableType.CalendarEventDefaultColor]: '#3A4864FF',
  [ThemeVariableType.MapboxStreetStyleUrl]: 'mapbox://styles/hypris/cm7kat8el00s401s7dstba3ec',
  [ThemeVariableType.MapPickerMarkerColor]: '#3399ff',
  [ThemeVariableType.MapPickerAreaColor]: '#b4d4ff',
  [ThemeVariableType.ShouldShowModalHeaderComponentBackground]: true,
  [ThemeVariableType.TimeTrackerChartBarColor]: '#558FEF',
  [ThemeVariableType.TimeTrackerChartBarBorderColor]: 'rgba(0,0,0,0.9)',
  // etykieta slupka bez obrysu (wystajace cyfry sa widoczne na ciemnym panelu)
  [ThemeVariableType.TimeTrackerChartLabelStrokeColor]: 'transparent',

  // T-946 PR 1A (tura 2): dokladnie to, co rysuje sie dzis. Tu tylko wartosci spoza motywu - chip sumy = literaly
  // z sumPlugin/pieSumPlugin, tooltip = domyslne Chart.js 4 (plugins.tooltip); legenda, siatka, modul i krok kanwy,
  // rog karteczki i marquee = inne zmienne motywu (getFiordNightDerivedVariables)
  [ThemeVariableType.ChartTotalBadgeBackgroundColor]: '#2C3747',
  [ThemeVariableType.ChartTotalBadgeTextColor]: '#E0E3E9',
  [ThemeVariableType.ChartTooltipBackgroundColor]: 'rgba(0,0,0,0.8)',
  [ThemeVariableType.ChartTooltipTitleColor]: '#fff',
  [ThemeVariableType.ChartTooltipBodyColor]: '#fff',
  [ThemeVariableType.ChartTooltipBorderColor]: 'rgba(0,0,0,0)',
  [ThemeVariableType.ChartTooltipBorderWidth]: 0,

  [ThemeVariableType.PaletteColors]: PaletteColors,
  [ThemeVariableType.PaletteColorMatrix]: PaletteColorMatrix,
}

export const FiordNightThemeVariables: IThemeVariables = {
  ...FiordNightBaseThemeVariables,
  ...getFiordNightDerivedVariables(FiordNightBaseThemeVariables),
}
